import {
  useDeleteApiAdministrationActionmodulesTemplatesActionModuleId,
  useDeleteApiAdministrationCompaniesCompanyIdActionmodulesActionModuleId,
  usePatchApiAdministrationActionmodulesTemplatesActionModuleId,
  usePatchApiAdministrationCompaniesCompanyIdActionmodulesActionModuleId,
  usePostApiAdministrationActionmodulesTemplates,
  usePostApiAdministrationActionmodulesTemplatesCreateGlobalTemplateFromActionModuleId,
  usePostApiAdministrationCompaniesCompanyIdActionmodules,
} from '@/api/generated/administrator-user/administrator-user'
import {
  useDeleteApiCompaniesCurrentActionmodulesActionModuleId,
  useDeleteApiCompaniesCurrentActionmodulesTemplatesActionModuleId,
  usePatchApiCompaniesCurrentActionmodulesActionModuleId,
  usePostApiCompaniesCurrentActionmodules, usePostApiCompaniesCurrentActionmodulesTemplates,
  usePostApiCompaniesCurrentActionmodulesTemplatesCreateLiveFromTemplateActionModuleId,
  usePostApiCompaniesCurrentActionmodulesTemplatesCreateTemplateFromActionModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import { PermissionEntityType, type CreateActionModuleRequest } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'

interface Args {
  isAdmin: boolean
  /** Required in admin scope; ignored (and absent) in CMU scope. */
  companyId?: number
}

/**
 * Save/delete for the action-module editor, branching on scope:
 * - Admin (`isAdmin`, with `companyId`): full CRUD via the administration endpoints.
 * - CMU (`!isAdmin`): create + edit + delete via `companies/current`, gated by the user's
 *   `Create`/`Delete` permissions (`canCreate`/`canDelete`). The CMU patch defaults to `metadataOnly=true`
 *   (metadata-only write) — the full editor must send `metadataOnly=false` so
 *   subtypes/reward/scheduling/assignment persist.
 *
 * All five generated hooks are instantiated unconditionally (rules of hooks); the picked
 * scope decides which one actually fires.
 */
export function useActionModuleMutations({ isAdmin, companyId }: Args) {
  const { userHasPermission, userHasEntityPermission } = usePermissions()
  const adminCreate = usePostApiAdministrationCompaniesCompanyIdActionmodules()
  const adminPatch = usePatchApiAdministrationCompaniesCompanyIdActionmodulesActionModuleId()
  const adminDelete = useDeleteApiAdministrationCompaniesCompanyIdActionmodulesActionModuleId()
  const cmuCreate = usePostApiCompaniesCurrentActionmodules()
  const cmuDelete = useDeleteApiCompaniesCurrentActionmodulesActionModuleId()
  const cmuPatch = usePatchApiCompaniesCurrentActionmodulesActionModuleId()
  const cmuCompanyTemplateCreate = usePostApiCompaniesCurrentActionmodulesTemplates()
  const adminTemplateCreate = usePostApiAdministrationActionmodulesTemplates()
  const cmuTemplateCreateLiveFromTemplate = usePostApiCompaniesCurrentActionmodulesTemplatesCreateLiveFromTemplateActionModuleId()
  const cmuTemplateCreateTemplateFromLive = usePostApiCompaniesCurrentActionmodulesTemplatesCreateTemplateFromActionModuleId()
  const adminTemplateCreateTemplateFromLive = usePostApiAdministrationActionmodulesTemplatesCreateGlobalTemplateFromActionModuleId()
  const adminTemplatePatch = usePatchApiAdministrationActionmodulesTemplatesActionModuleId()
  const adminTemplateDelete = useDeleteApiAdministrationActionmodulesTemplatesActionModuleId()
  const cmuTemplateDelete = useDeleteApiCompaniesCurrentActionmodulesTemplatesActionModuleId()

  const isSaving =
    adminCreate.isPending ||
    adminPatch.isPending ||
    adminDelete.isPending ||
    cmuDelete.isPending ||
    cmuCreate.isPending ||
    cmuPatch.isPending ||
    cmuCompanyTemplateCreate.isPending ||
    adminTemplateCreate.isPending ||
    cmuTemplateCreateLiveFromTemplate.isPending ||
    cmuTemplateCreateTemplateFromLive.isPending ||
    adminTemplateCreateTemplateFromLive.isPending ||
    adminTemplatePatch.isPending ||
    adminTemplateDelete.isPending ||
    cmuTemplateDelete.isPending

  function requireAdmin(): number {
    if (!isAdmin || companyId == null) {
      throw new Error('This operation is only available in admin scope with a company id.')
    }
    return companyId
  }

  async function create(request: CreateActionModuleRequest) {
    if (isAdmin) {
      const cid = requireAdmin()
      return adminCreate.mutateAsync({ companyId: cid, data: request })
    }
    return cmuCreate.mutateAsync({ data: request })
  }

  async function patch(actionModuleId: number, request: CreateActionModuleRequest) {
    if (isAdmin) {
      const cid = requireAdmin()
      return adminPatch.mutateAsync({ companyId: cid, actionModuleId, data: request })
    }
    // CMU full-editor save: metadataOnly=false so the polymorphic slices persist.
    return cmuPatch.mutateAsync({ actionModuleId, data: request, params: { metadataOnly: false } })
  }

  async function remove(actionModuleId: number) {
    if (isAdmin) {
      const cid = requireAdmin()
      return adminDelete.mutateAsync({ companyId: cid, actionModuleId })
    }
    return cmuDelete.mutateAsync({ actionModuleId })
  }

  async function createCompanyTemplate(request: CreateActionModuleRequest, validateOnly: boolean){
    return cmuCompanyTemplateCreate.mutateAsync({ data: request, params: {validateOnly: validateOnly}})
  }
  async function createAdminTemplate(createGlobalTemplate: boolean, request: CreateActionModuleRequest, validateOnly: boolean){
    if (createGlobalTemplate){
      return adminTemplateCreate.mutateAsync({data: request, params: {validateOnly: validateOnly}})
    }
    return adminTemplateCreate.mutateAsync({data: request, params: {companyId, validateOnly: validateOnly}})
  }
  async function createLiveActionModuleFromTemplate(actionModuleId: number){
    return cmuTemplateCreateLiveFromTemplate.mutateAsync({actionModuleId})
  }
  async function createCompanyTemplateFromLiveActionModule(actionModuleId: number){
    return cmuTemplateCreateTemplateFromLive.mutateAsync({actionModuleId})
  }
  async function createGlobalTemplateFromLiveActionModule(actionModuleId: number){
    return adminTemplateCreateTemplateFromLive.mutateAsync({actionModuleId})
  }
  async function patchAdminTemplate(isGlobalTemplate: boolean, actionModuleId: number, request: CreateActionModuleRequest){
    const cid = requireAdmin()
    if (isGlobalTemplate){
      return adminTemplatePatch.mutateAsync({ actionModuleId, data: request})
    }
      return adminTemplatePatch.mutateAsync({ actionModuleId, data: request, params: { companyId: cid } })
  }
  async function removeAdminTemplate(isGlobalTemplate: boolean, actionModuleId: number){
    const cid = requireAdmin()
    if (isGlobalTemplate) {
      return adminTemplateDelete.mutateAsync({ actionModuleId})
    }
    return adminTemplateDelete.mutateAsync({ actionModuleId, params: { companyId: cid }})
  }
  async function removeCompanyTemplate(actionModuleId: number){
    return cmuTemplateDelete.mutateAsync({ actionModuleId})
  }

  return {
    create,
    patch,
    remove,
    createCompanyTemplate,
    createAdminTemplate,
    createLiveActionModuleFromTemplate,
    createCompanyTemplateFromLiveActionModule,
    createGlobalTemplateFromLiveActionModule,
    patchAdminTemplate,
    removeAdminTemplate,
    removeCompanyTemplate,
    isSaving,
    canCreate: isAdmin || userHasPermission(PermissionEntityType.ActionModules, 'Create'),
    canDelete: (actionModuleId: number) =>
      isAdmin || userHasEntityPermission(PermissionEntityType.ActionModules, 'Delete', actionModuleId),
  }
}
